import { CoachProfileHeader } from '@efdigital/ll-addon-coaching';
import { isAwaitingCoachConfirmation } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-capabilities';
import { isCancelled } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-lifecycle';
import { Heading } from '@efdigital/ll-addon-ui';
import { Spinner } from '@efdigital/ll-xceed-v2/components/application/spinner.gts';
import SessionBookingFlow from '@efdigital/ll-xceed-v2/components/coaching/coachee/session-booking-flow.gts';
import SessionCard from '@efdigital/ll-xceed-v2/components/coaching/coachee/session-card.gts';
import { CreditBalance } from '@efdigital/ll-xceed-v2/components/coaching/credit-balance.gts';
import type { CoachingCoacheeCohortCoachesCoachModel } from '@efdigital/ll-xceed-v2/routes/coaching/coachee/cohort/coaches/coach';
import { PAGE_WRAPPER_CONTENT } from '@efdigital/ll-xceed-v2/utils/layout';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pageTitle } from 'ember-page-title';

interface BookChemistrySignature {
  Args: {
    model: CoachingCoacheeCohortCoachesCoachModel;
    controller: unknown;
  };
}

export default class BookChemistryTemplate extends Component<BookChemistrySignature> {
  @service declare router: RouterService;
  @tracked saving = false;

  refresh = () => {
    this.router.refresh('coaching.coachee.cohort.coaches.coach.book-chemistry');
  };

  get existingChemistrySessions() {
    return this.args.model.planner.getChemistrySessions(
      this.args.model.coach.user
    );
  }

  get activeChemistrySessions() {
    return this.existingChemistrySessions.filter(
      (session) =>
        !isCancelled(session.status) && session.status !== 'booking_abandoned'
    );
  }

  get inactiveChemistrySessions() {
    return this.existingChemistrySessions.filter(
      (session) =>
        isCancelled(session.status) || session.status === 'booking_abandoned'
    );
  }

  get chemistrySessionSecondaryText() {
    if (
      this.activeChemistrySessions.some((session) =>
        isAwaitingCoachConfirmation(session.status)
      )
    ) {
      return 'Your coach must accept the booking before the session can take place.';
    }

    return 'Your coach has accepted this chemistry session.';
  }

  get hasChemistrySessions() {
    return this.existingChemistrySessions.length > 0;
  }

  bookSession = async (
    start: string,
    end: string,
    notes: string | undefined
  ) => {
    const idempotencyKey = crypto.randomUUID();
    this.saving = true;
    try {
      await this.args.model.planner.bookSession(
        this.args.model.coach.user,
        'chemistry',
        idempotencyKey,
        start,
        end,
        notes
      );
    } catch (error) {
      console.error(error);
      if (error instanceof Error) {
        window.alert(error.message);
      }
    } finally {
      this.saving = false;
    }
  };

  <template>
    {{pageTitle "Book chemistry session"}}

    <div class={{PAGE_WRAPPER_CONTENT}}>
      <CoachProfileHeader
        @coach={{@model.coach}}
        @partnershipStatus={{@model.planner.getStatusForCoach
          @model.coach.user
        }}
        class="mb-5"
      />

      {{#if (@model.planner.canBookChemistrySession @model.coach.user)}}
        <div
          class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3"
        >
          <Heading
            @size="sm"
            @text="Book chemistry session"
            @secondary="Enter notes, then select a time and date for your chemistry session."
            @class="mb-0"
          />
          <CreditBalance
            @remaining={{@model.planner.quota.chemistry.remaining}}
            @noun="chemistry call"
            class="flex-shrink-0"
          />
        </div>
        <div class="card rounded-4 shadow-sm border p-3 p-lg-4">
          <div class="card-body">
            {{#if this.saving}}
              <Spinner />
            {{else}}
              <SessionBookingFlow
                @coachId={{@model.coach.user}}
                @coachingConfig={{@model.coachingConfig}}
                @sessionLengthMins={{@model.coachingConfig.chemistrySessionLengthMins}}
                @onSlotSelected={{this.bookSession}}
              />
            {{/if}}
          </div>
        </div>
      {{else}}
        {{#if this.activeChemistrySessions.length}}
          <Heading
            @size="sm"
            @text="Chemistry session"
            @secondary={{this.chemistrySessionSecondaryText}}
          />
          <div class="d-flex flex-column gap-4">
            {{#each this.activeChemistrySessions as |existingSession|}}
              <SessionCard
                @session={{existingSession}}
                @coach={{@model.coach}}
                @coachingConfig={{@model.coachingConfig}}
                @planner={{@model.planner}}
                @onChanged={{this.refresh}}
              />
            {{/each}}
          </div>
        {{/if}}

        {{#if this.inactiveChemistrySessions.length}}
          <div class="{{if this.activeChemistrySessions.length 'mt-4'}}">
            <h3 class="gud-eyebrow">History</h3>
            <div class="d-flex flex-column gap-4">
              {{#each this.inactiveChemistrySessions as |existingSession|}}
                <SessionCard
                  @session={{existingSession}}
                  @coach={{@model.coach}}
                  @coachingConfig={{@model.coachingConfig}}
                  @planner={{@model.planner}}
                  @onChanged={{this.refresh}}
                />
              {{/each}}
            </div>
          </div>
        {{/if}}

        {{#unless this.hasChemistrySessions}}
          <div class="alert alert-danger" role="alert">
            <p class="mb-0">Sorry, you cannot book a chemistry session with this
              coach.</p>
          </div>
        {{/unless}}
      {{/if}}
    </div>
  </template>
}
